import { useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';
import { INACTIVITY_TIMEOUT, INACTIVITY_WARNING_TIME } from '../constants/constants';
import {
  peekLogoutRedirectPath,
  setPostLoginReturnPath,
  readSharedLastActivity,
  writeSharedLastActivity,
  applyInactivityLogoutBroadcast,
  clearInactivityLogoutBroadcast,
  SHARED_LAST_ACTIVITY_KEY,
  INACTIVITY_LOGOUT_BROADCAST_KEY
} from '../utils/authSession';
import { signOutWithTimeout } from '../utils/authUtils';

const HEARTBEAT_INTERVAL_MS = 15 * 1000;
const ACTIVITY_DEBOUNCE_MS = 1000;
const SHARED_ACTIVITY_WRITE_INTERVAL_MS = 5 * 1000;

/**
 * Hook to handle automatic logout after inactivity
 * Tracks user activity and shows warning before logging out
 *
 * Firebase auth is shared by every tab, so signing out in one tab signs out all of
 * them. Activity is therefore shared through localStorage: a tab only logs out when
 * no open tab has seen activity for the full timeout.
 *
 * @param {boolean} isAuthenticated - Whether user is currently authenticated
 * @param {Function} onLogout - Callback when user is logged out (runs before signOut)
 * @param {Function} onWarning - Callback when warning should be shown (receives secondsRemaining)
 * @param {Function} onWarningDismiss - Callback when warning is dismissed (user extends session)
 * @param {{ enabled?: boolean, timeoutMs?: number }} [options]
 */
export const useInactivityLogout = (
  isAuthenticated,
  onLogout,
  onWarning,
  onWarningDismiss,
  options = {}
) => {
  const { enabled = true, timeoutMs = INACTIVITY_TIMEOUT } = options;
  const enabledRef = useRef(enabled);
  const timeoutMsRef = useRef(timeoutMs);

  useEffect(() => {
    enabledRef.current = enabled;
    timeoutMsRef.current = timeoutMs;
  }, [enabled, timeoutMs]);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const lastSharedWriteRef = useRef(0);
  const isWarningShownRef = useRef(false);
  const warningCountdownRef = useRef(null);
  const heartbeatRef = useRef(null);
  const loggingOutRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const activityDebounceRef = useRef(null);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const publishActivity = useCallback((timestamp, { force = false } = {}) => {
    if (!force && timestamp - lastSharedWriteRef.current < SHARED_ACTIVITY_WRITE_INTERVAL_MS) {
      return;
    }
    lastSharedWriteRef.current = timestamp;
    writeSharedLastActivity(timestamp);
  }, []);

  /** Pull in newer activity from other tabs. Returns true when it moved lastActivityRef forward. */
  const syncSharedActivity = useCallback(() => {
    const shared = readSharedLastActivity();
    if (shared !== null && shared > lastActivityRef.current) {
      lastActivityRef.current = shared;
      return true;
    }
    return false;
  }, []);

  const clearInactivityTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (warningCountdownRef.current) {
      clearInterval(warningCountdownRef.current);
      warningCountdownRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearInactivityTimeouts();
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, [clearInactivityTimeouts]);

  const handleLogout = useCallback(async () => {
    if (loggingOutRef.current) {
      return;
    }
    loggingOutRef.current = true;
    clearAllTimers();

    let redirectPath = '/login';
    try {
      logger.info('Auto-logging out due to inactivity');
      if (onLogout) {
        onLogout();
      }
      const returnPath = `${window.location.pathname || ''}${window.location.search || ''}`;
      if (returnPath && returnPath !== '/session-expired' && returnPath !== '/login') {
        setPostLoginReturnPath(returnPath);
      }
      // Peek, not consume: SessionExpired reads the reason after this hard redirect
      // and clears it once sign-out is confirmed.
      redirectPath = peekLogoutRedirectPath() || '/login';
      await signOutWithTimeout();
    } catch (error) {
      logger.error('Error during auto-logout:', error);
      redirectPath = peekLogoutRedirectPath() || redirectPath;
    } finally {
      window.location.replace(redirectPath);
    }
  }, [clearAllTimers, onLogout]);

  const evaluateInactivityRef = useRef(() => {});

  const resetTimer = useCallback((activityAt = Date.now()) => {
    clearInactivityTimeouts();
    loggingOutRef.current = false;

    if (isWarningShownRef.current) {
      isWarningShownRef.current = false;
      if (onWarningDismiss) {
        onWarningDismiss();
      }
    }

    if (!enabledRef.current || !isAuthenticatedRef.current) {
      return;
    }

    lastActivityRef.current = Math.max(lastActivityRef.current, activityAt);

    const inactivityTimeout = timeoutMsRef.current;
    const elapsed = Math.max(0, Date.now() - lastActivityRef.current);
    warningTimeoutRef.current = setTimeout(() => {
      evaluateInactivityRef.current();
    }, Math.max(0, inactivityTimeout - INACTIVITY_WARNING_TIME - elapsed));

    timeoutRef.current = setTimeout(() => {
      evaluateInactivityRef.current();
    }, Math.max(0, inactivityTimeout - elapsed));
  }, [onWarningDismiss, clearInactivityTimeouts]);

  evaluateInactivityRef.current = () => {
    if (!enabledRef.current || !isAuthenticatedRef.current || loggingOutRef.current) {
      return;
    }

    if (syncSharedActivity()) {
      resetTimer(lastActivityRef.current);
      return;
    }

    const inactivityTimeout = timeoutMsRef.current;
    const elapsed = Date.now() - lastActivityRef.current;

    if (elapsed >= inactivityTimeout) {
      handleLogout();
      return;
    }

    const warningThreshold = inactivityTimeout - INACTIVITY_WARNING_TIME;
    if (elapsed >= warningThreshold) {
      const secondsRemaining = Math.max(1, Math.ceil((inactivityTimeout - elapsed) / 1000));
      if (!isWarningShownRef.current) {
        isWarningShownRef.current = true;
      }
      if (onWarning) {
        onWarning(secondsRemaining);
      }
      if (!warningCountdownRef.current) {
        warningCountdownRef.current = setInterval(() => {
          evaluateInactivityRef.current();
        }, 1000);
      }
      return;
    }

    if (isWarningShownRef.current) {
      isWarningShownRef.current = false;
      if (warningCountdownRef.current) {
        clearInterval(warningCountdownRef.current);
        warningCountdownRef.current = null;
      }
      if (onWarningDismiss) {
        onWarningDismiss();
      }
    }
  };

  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    publishActivity(now);

    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
    }

    activityDebounceRef.current = setTimeout(() => {
      activityDebounceRef.current = null;
      publishActivity(lastActivityRef.current, { force: true });
      resetTimer();
    }, ACTIVITY_DEBOUNCE_MS);
  }, [resetTimer, publishActivity]);

  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      clearAllTimers();
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
        activityDebounceRef.current = null;
      }
      isWarningShownRef.current = false;
      loggingOutRef.current = false;
      return undefined;
    }

    const startedAt = Date.now();
    lastActivityRef.current = startedAt;
    clearInactivityLogoutBroadcast();
    publishActivity(startedAt, { force: true });
    resetTimer();

    heartbeatRef.current = setInterval(() => {
      evaluateInactivityRef.current();
    }, HEARTBEAT_INTERVAL_MS);

    const windowEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'keyup',
      'input',
      'paste',
      'touchstart',
      'touchend',
      'pointerdown',
      'click',
      'wheel'
    ];

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        evaluateInactivityRef.current();
      }
    };
    const onPageShow = () => evaluateInactivityRef.current();
    const onFocus = () => evaluateInactivityRef.current();

    const onStorage = (event) => {
      if (event.key === INACTIVITY_LOGOUT_BROADCAST_KEY) {
        applyInactivityLogoutBroadcast();
        return;
      }
      if (event.key !== SHARED_LAST_ACTIVITY_KEY || loggingOutRef.current) {
        return;
      }
      const shared = readSharedLastActivity();
      if (shared !== null && shared > lastActivityRef.current) {
        resetTimer(shared);
      }
    };

    windowEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    // Scroll does not bubble; capture on document catches nested overflow containers.
    document.addEventListener('scroll', handleActivity, { capture: true, passive: true });
    document.addEventListener('selectionchange', handleActivity);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);

    return () => {
      windowEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('scroll', handleActivity, { capture: true });
      document.removeEventListener('selectionchange', handleActivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      clearAllTimers();
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
        activityDebounceRef.current = null;
      }
    };
  }, [isAuthenticated, enabled, resetTimer, handleActivity, clearAllTimers, publishActivity]);

  const extendSession = useCallback(() => {
    const now = Date.now();
    publishActivity(now, { force: true });
    resetTimer(now);
  }, [resetTimer, publishActivity]);

  return { extendSession };
};
